import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Trash2, Edit, Check, X } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useNotify } from '@/hooks/useNotify';
import { CategoryWithMarkupTiers, CategoryMarkupTier } from '@/types/category-markup-tiers';
import { useAuth } from '@/contexts/useAuth';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

interface CategoryMarkupTiersProps {
  category: CategoryWithMarkupTiers;
  onUpdate: () => void;
}

const CategoryMarkupTiers: React.FC<CategoryMarkupTiersProps> = ({ category, onUpdate }) => {
  const [minAmount, setMinAmount] = useState<number>(0);
  const [maxAmount, setMaxAmount] = useState<string>('');
  const [markupPercentage, setMarkupPercentage] = useState<number>(0);
  const [tiers, setTiers] = useState<CategoryMarkupTier[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [tierToDelete, setTierToDelete] = useState<CategoryMarkupTier | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingTier, setEditingTier] = useState<CategoryMarkupTier | null>(null);
  const notify = useNotify();
  const { profile } = useAuth();

  useEffect(() => {
    if (category) {
      fetchTiers();
    }
  }, [category]);

  const fetchTiers = async () => {
    try {
      setIsLoading(true);
      
      // Prepare to fetch both company-specific and default tiers
      let allTiers: CategoryMarkupTier[] = [];
      
      // 1. Get company-specific tiers
      if (profile?.company_id) {
        const { data: companyTiers, error: companyError } = await supabase
          .from('category_markup_tiers')
          .select('*')
          .eq('category_id', category.id)
          .eq('company_id', profile.company_id)
          .order('min_amount', { ascending: true });
        
        if (companyError) {
          console.error("Error fetching company markup tiers:", companyError);
          throw companyError;
        }
        
        if (companyTiers) {
          allTiers = [...companyTiers];
        }
      }
      
      // 2. Get default tiers if needed (for default categories)
      if (category.is_default) {
        const { data: defaultTiers, error: defaultError } = await supabase
          .from('category_markup_tiers')
          .select('*')
          .eq('category_id', category.id)
          .is('company_id', null)
          .order('min_amount', { ascending: true });
        
        if (defaultError) {
          console.error("Error fetching default markup tiers:", defaultError);
          throw defaultError;
        }
        
        if (defaultTiers) {
          // Add default tiers that don't overlap with company tiers
          defaultTiers.forEach(defaultTier => {
            if (!allTiers.some(tier => 
              tier.min_amount === defaultTier.min_amount && 
              tier.max_amount === defaultTier.max_amount
            )) {
              allTiers.push(defaultTier);
            }
          });
        }
      }
      
      // Sort all tiers by min_amount
      allTiers.sort((a, b) => a.min_amount - b.min_amount);
      setTiers(allTiers);
      
    } catch (error) {
      console.error("Error fetching markup tiers:", error);
      notify.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const startEdit = (index: number) => {
    setEditingIndex(index);
    setEditingTier({ ...tiers[index] });
  };

  const cancelEdit = () => {
    setEditingIndex(null);
    setEditingTier(null);
  };

  const saveEdit = async () => {
    if (!editingTier || editingIndex === null) return;

    try {
      // Calculate margin from markup
      const marginPercentage = (editingTier.markup_percentage / (100 + editingTier.markup_percentage)) * 100;
      
      const { error } = await supabase
        .from('category_markup_tiers')
        .update({
          min_amount: editingTier.min_amount,
          max_amount: editingTier.max_amount,
          markup_percentage: editingTier.markup_percentage,
          margin_percentage: parseFloat(marginPercentage.toFixed(2))
        })
        .eq('id', editingTier.id);

      if (error) throw error;

      notify.success("Tier updated successfully");
      
      setEditingIndex(null);
      setEditingTier(null);
      fetchTiers();
      onUpdate();

    } catch (error) {
      console.error('Error updating markup tier:', error);
      notify.error(error);
    }
  };

  const addTier = async () => {
    if (minAmount < 0) {
      notify.warning("Minimum amount cannot be negative");
      return;
    }

    if (maxAmount && parseFloat(maxAmount) <= minAmount) {
      notify.warning("Maximum amount must be greater than minimum amount");
      return;
    }

    if (markupPercentage < 0) {
      notify.warning("Markup percentage cannot be negative");
      return;
    }

    try {
      // Calculate margin from markup
      const marginPercentage = (markupPercentage / (100 + markupPercentage)) * 100;
      
      const { data, error } = await supabase
        .from('category_markup_tiers')
        .insert([
          {
            category_id: category.id,
            company_id: profile?.company_id,
            min_amount: minAmount,
            max_amount: maxAmount ? parseFloat(maxAmount) : null,
            markup_percentage: markupPercentage,
            margin_percentage: parseFloat(marginPercentage.toFixed(2))
          }
        ]);

      if (error) throw error;

      notify.success("Tier added successfully");
      
      // Reset inputs
      setMinAmount(0);
      setMaxAmount('');
      setMarkupPercentage(0);
      
      // Refresh tiers
      fetchTiers();
      onUpdate();

    } catch (error) {
      console.error('Error adding markup tier:', error);
      notify.error(error);
    }
  };

  const deleteTier = async (tier: CategoryMarkupTier) => {
    try {
      const { error } = await supabase
        .from('category_markup_tiers')
        .delete()
        .eq('id', tier.id);

      if (error) throw error;

      notify.success("Tier deleted successfully");
      
      // Clear the tierToDelete state
      setTierToDelete(null);
      
      // Refresh tiers
      fetchTiers();
      onUpdate();

    } catch (error) {
      console.error('Error deleting markup tier:', error);
      notify.error(error);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Markup Tiers for {category.name}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="text-sm font-medium">Min Amount (R)</label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={minAmount}
                onChange={(e) => setMinAmount(parseFloat(e.target.value))}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Max Amount (R, optional)</label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={maxAmount}
                onChange={(e) => setMaxAmount(e.target.value)}
                placeholder="No maximum"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Markup (%)</label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={markupPercentage}
                onChange={(e) => setMarkupPercentage(parseFloat(e.target.value))}
              />
            </div>
            <div className="flex items-end">
              <Button onClick={addTier} className="w-full">
                <Plus className="h-4 w-4 mr-2" />
                Add Tier
              </Button>
            </div>
          </div>

          {isLoading ? (
            <div className="py-4 text-center">Loading markup tiers...</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Amount Range (R)</TableHead>
                  <TableHead>Markup (%)</TableHead>
                  <TableHead>Margin (%)</TableHead>
                  <TableHead className="w-32">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tiers.length > 0 ? (
                  tiers.map((tier, index) => (
                    <TableRow key={tier.id}>
                      <TableCell>
                        {editingIndex === index ? (
                          <div className="flex gap-2">
                            <Input
                              type="number"
                              value={editingTier?.min_amount ?? ''}
                              onChange={(e) => {
                                const val = e.target.value;
                                setEditingTier(prev => prev ? { ...prev, min_amount: val === '' ? ('' as any) : (parseFloat(val) || 0) } : null);
                              }}
                              onBlur={() => { if ((editingTier?.min_amount as any) === '') setEditingTier(prev => prev ? { ...prev, min_amount: 0 } : null); }}
                              step="0.01"
                              min="0"
                              className="w-20"
                            />
                            <span className="self-center">to</span>
                            <Input
                              type="number"
                              value={editingTier?.max_amount || ''}
                              onChange={(e) => setEditingTier(prev => prev ? { 
                                ...prev, 
                                max_amount: e.target.value ? parseFloat(e.target.value) : null 
                              } : null)}
                              step="0.01"
                              min="0"
                              placeholder="∞"
                              className="w-20"
                            />
                          </div>
                        ) : (
                          `${tier.min_amount}+ ${tier.max_amount ? ` to ${tier.max_amount}` : ''}`
                        )}
                      </TableCell>
                      <TableCell>
                        {editingIndex === index ? (
                          <Input
                            type="number"
                            value={editingTier?.markup_percentage ?? ''}
                            onChange={(e) => {
                              const val = e.target.value;
                              setEditingTier(prev => prev ? { ...prev, markup_percentage: val === '' ? ('' as any) : (parseFloat(val) || 0) } : null);
                            }}
                            onBlur={() => { if ((editingTier?.markup_percentage as any) === '') setEditingTier(prev => prev ? { ...prev, markup_percentage: 0 } : null); }}
                            step="0.01"
                            min="0"
                            className="w-24"
                          />
                        ) : (
                          `${tier.markup_percentage.toFixed(2)}%`
                        )}
                      </TableCell>
                      <TableCell>{tier.margin_percentage.toFixed(2)}%</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {editingIndex === index ? (
                            <>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={saveEdit}
                              >
                                <Check className="h-4 w-4 text-green-600" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={cancelEdit}
                              >
                                <X className="h-4 w-4 text-red-600" />
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => startEdit(index)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete Markup Tier</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Are you sure you want to delete this markup tier for amounts {tier.min_amount}+ {tier.max_amount ? ` to ${tier.max_amount}` : ''}? 
                                      This action cannot be undone.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction onClick={() => deleteTier(tier)}>
                                      Delete
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-4">
                      No markup tiers defined for this category
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default CategoryMarkupTiers;
